import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, addDoc, updateDoc, doc, deleteDoc, orderBy, where } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { ownerStamp } from '../recordOwner';
import HeaderActionsPortal from './HeaderActionsPortal';
import ModuleEditPermissions from './ModuleEditPermissions';
import { useT } from '../i18n.jsx';
import '../theme-palette.css';
import { Plus, Edit2, Trash2, CheckCircle, XCircle, Search, Laptop, Key, RefreshCw, AlertCircle, Clock, Download, Upload, X, Shield } from 'lucide-react';
import { useOutletContext } from 'react-router-dom';
import Papa from 'papaparse';
import { formatDateTime } from '../utils/dateFormatter';

const MDMAdmin = () => {
  const { t } = useT();
  // Booking statuses share their keys with the borrower's screen, so both sides of one record
  // read the same word.
  const bookingStatusWord = (v) => t(
    `mdmUser.statuses.${String(v || '').toLowerCase().replace(/[^a-z]/g, '')}`, {}, v,
  );
  const [activeTab, setActiveTab] = useState('assets');
  const [assets, setAssets] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  // New Asset Form State
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAssetId, setEditingAssetId] = useState(null);
  const [newAsset, setNewAsset] = useState({ assetId: '', name: '', linkedInventoryId: '', type: 'Device', status: 'Available', autoApprove: false, quantity: 1 });
  const [itInventory, setItInventory] = useState([]);
  const [assetSource, setAssetSource] = useState('inventory');
  
  const { activeSchoolId, isMobile, closeSidebar } = useOutletContext();

  // Also clears assetSource, which the Add button never reset: editing a custom-named asset and then
  // cancelling used to leave the next Add stuck on "Custom Item" instead of "From IT Inventory".
  const closeAssetForm = () => {
    setShowAddForm(false);
    setEditingAssetId(null);
    setAssetSource('inventory');
  };

  // Shared dialog behaviour: retract the mobile sidebar (a fixed overlay that would otherwise sit on
  // top of the sheet), stop the page behind from scrolling, and close on Escape.
  useEffect(() => {
    if (!showAddForm) return;
    if (isMobile) closeSidebar?.();

    const onKey = (e) => { if (e.key === 'Escape') closeAssetForm(); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [showAddForm, isMobile, closeSidebar]);

  useEffect(() => {
    if (activeSchoolId) fetchData();
  }, [activeSchoolId]);

  const fetchData = async () => {
    if (!activeSchoolId) return;
    setLoading(true);
    try {
      // Bookings
      const bSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'mdm_bookings')));
      const bList = [];
      bSnap.forEach(d => bList.push({ id: d.id, ...d.data() }));
      bList.sort((a, b) => (b.requestDate?.seconds || 0) - (a.requestDate?.seconds || 0));
      setBookings(bList);

      const now = new Date();
      const activeBookings = bList.filter(b => b.status === 'Reserved' && b.startDate && b.endDate && (b.startDate.toDate ? b.startDate.toDate() : new Date(b.startDate)) <= now && (b.endDate.toDate ? b.endDate.toDate() : new Date(b.endDate)) >= now);

      // Assets
      const aSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'mdm_assets')));
      const aList = [];
      aSnap.forEach(d => {
         const data = d.data();
         const usedQty = activeBookings.filter(b => b.assetId === d.id).reduce((sum, b) => sum + (b.quantity || 1), 0);
         const currentAvailable = Math.max(0, (data.quantity || 1) - usedQty);
         aList.push({ id: d.id, ...data, dynamicAvailableQty: currentAvailable });
      });
      setAssets(aList);

      // IT Inventory
      const invSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'inventory')));
      const invList = [];
      invSnap.forEach(d => {
        const item = d.data();
        const isIT = item.category === 'IT' || item.category === 'IT Equipment' || String(item.committee).includes('IT');
        if (item.status === 'Active' && isIT) {
          invList.push({ uid: d.id, ...item });
        }
      });
      setItInventory(invList);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const handleEditAsset = (asset) => {
    setEditingAssetId(asset.id);
    setAssetSource(asset.linkedInventoryId ? 'inventory' : 'custom');
    setNewAsset({
      assetId: asset.assetId || '',
      name: asset.name || '',
      linkedInventoryId: asset.linkedInventoryId || '',
      type: asset.type || 'Device',
      status: asset.status || 'Available',
      autoApprove: asset.autoApprove || false,
      quantity: asset.quantity || 1
    });
    setShowAddForm(true);
  };

  const handleAddAsset = async (e) => {
    e.preventDefault();
    if (!newAsset.name || !activeSchoolId) return;

    if (assetSource === 'inventory' && newAsset.linkedInventoryId) {
      const selectedItem = itInventory.find(i => i.id === newAsset.linkedInventoryId);
      if (selectedItem && Number(newAsset.quantity) > Number(selectedItem.quantity)) {
        alert(t('mdmAdmin.quantityExceeds', { wanted: newAsset.quantity, available: selectedItem.quantity }));
        return;
      }
    }

    try {
      if (editingAssetId) {
        await updateDoc(doc(db, 'schools', activeSchoolId, 'mdm_assets', editingAssetId), { ...newAsset, schoolId: activeSchoolId });
      } else {
        const finalAssetId = newAsset.assetId || `MDM-${Date.now().toString().slice(-6)}-${Math.floor(Math.random()*100)}`;
        await addDoc(collection(db, 'schools', activeSchoolId, 'mdm_assets'), { ...ownerStamp(), ...newAsset, assetId: finalAssetId, schoolId: activeSchoolId });
      }
      setNewAsset({ assetId: '', name: '', linkedInventoryId: '', type: 'Device', status: 'Available', autoApprove: false, quantity: 1 });
      setShowAddForm(false);
      setEditingAssetId(null);
      fetchData();
    } catch (e) {
      alert(t('mdmAdmin.addFailed', { message: e.message }));
    }
  };

  const handleExportCSV = () => {
    const csvData = assets.map(a => ({
      'Asset ID': a.assetId || '',
      'Asset Name': a.name || '',
      'Linked Item ID': a.linkedInventoryId || '',
      'Type': a.type || 'Device',
      'Quantity': a.quantity || 1,
      'Status': a.status || 'Available',
      'Auto Approve': a.autoApprove ? 'TRUE' : 'FALSE'
    }));

    const csv = Papa.unparse(csvData);
    const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `MDM_Assets_Export.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const parsedData = results.data;
        let successCount = 0;
        let errorCount = 0;

        for (const row of parsedData) {
          const assetId = row['Asset ID']?.trim();
          const name = row['Asset Name']?.trim();
          if (!name) continue;

          const assetData = {
            schoolId: activeSchoolId,
            name: name,
            linkedInventoryId: row['Linked Item ID']?.trim() || '',
            type: row['Type']?.trim() || 'Device',
            quantity: parseInt(row['Quantity'], 10) || 1,
            status: row['Status']?.trim() || 'Available',
            autoApprove: row['Auto Approve']?.trim().toUpperCase() === 'TRUE'
          };

          try {
            if (assetId) {
              const existingAsset = assets.find(a => a.assetId === assetId);
              if (existingAsset) {
                assetData.assetId = assetId;
                await updateDoc(doc(db, 'schools', activeSchoolId, 'mdm_assets', existingAsset.id), assetData);
              } else {
                assetData.assetId = assetId;
                await addDoc(collection(db, 'schools', activeSchoolId, 'mdm_assets'), { ...assetData, ...ownerStamp() });
              }
            } else {
              assetData.assetId = `MDM-${Date.now().toString().slice(-6)}-${Math.floor(Math.random() * 100)}`;
              await addDoc(collection(db, 'schools', activeSchoolId, 'mdm_assets'), { ...assetData, ...ownerStamp() });
            }
            successCount++;
          } catch (err) {
            console.error("Error importing row:", row, err);
            errorCount++;
          }
        }
        
        alert(t('mdmAdmin.imported', { ok: successCount, failed: errorCount }));
        fetchData();
        e.target.value = null;
      },
      error: (err) => {
        alert(t('mdmAdmin.parseFailed', { message: err.message }));
      }
    });
  };

  const handleDeleteAsset = async (id) => {
    if (!window.confirm(t('mdmAdmin.deleteConfirm'))) return;
    try {
      await deleteDoc(doc(db, 'schools', activeSchoolId, 'mdm_assets', id));
      fetchData();
    } catch (e) {
      alert(t('mdmAdmin.deleteFailed', { message: e.message }));
    }
  };

  const handleBookingAction = async (bookingId, newStatus) => {
    try {
      await updateDoc(doc(db, 'schools', activeSchoolId, 'mdm_bookings', bookingId), { status: newStatus });
      fetchData();
    } catch (e) {
      alert(t('mdmAdmin.error', { message: e.message }));
    }
  };

  const handleForceReturn = async (assetId) => {
    if (!window.confirm(t('mdmAdmin.forceReturnConfirm'))) return;
    try {
      const now = new Date();
      const activeBookings = bookings.filter(b => b.assetId === assetId && b.status === 'Reserved' && b.startDate && b.endDate && (b.startDate.toDate ? b.startDate.toDate() : new Date(b.startDate)) <= now && (b.endDate.toDate ? b.endDate.toDate() : new Date(b.endDate)) >= now);
      for (const b of activeBookings) {
        await updateDoc(doc(db, 'schools', activeSchoolId, 'mdm_bookings', b.id), { status: 'Returned' });
      }
      fetchData();
    } catch (e) {
      alert(t('mdmAdmin.error', { message: e.message }));
    }
  };

  return (
    <div className="animate-fade-in new-theme" style={{ width: '100%' }}>
      
      {/* Tabs */}
      <div className="theme-tabs-bar">
        <div className="theme-tabs-group">
          {[
            { id: 'assets', label: t('mdmAdmin.tabs.inventory'), icon: Laptop },
            { id: 'bookings', label: t('mdmAdmin.tabs.approvals'), icon: AlertCircle },
            { id: 'settings', label: t('mdmAdmin.tabs.settings'), icon: Shield }
          ].map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`theme-tab ${isActive ? 'active' : ''}`}
              >
                <div className="theme-tab-icon">
                  <Icon size={20} />
                </div>
                {tab.label}
              </button>
            )
          })}
        </div>
        
        <HeaderActionsPortal>
        <div style={{ display: 'flex', gap: '1rem', padding: '0.5rem 0' }}>
          {activeTab === 'assets' && (
            <>
              <button onClick={handleExportCSV} title={t('mdmAdmin.exportCsv')} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.5rem 1rem', fontSize: '0.875rem', backgroundColor: 'var(--color-bg-app)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', cursor: 'pointer', color: 'var(--color-text-main)' }}>
                <Download size={16} />
              </button>
              <label title={t('mdmAdmin.importCsv')} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.5rem 1rem', fontSize: '0.875rem', backgroundColor: 'var(--color-bg-app)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', cursor: 'pointer', color: 'var(--color-text-main)', margin: 0 }}>
                <Upload size={16} />
                <input type="file" accept=".csv" style={{ display: 'none' }} onChange={handleImportCSV} />
              </label>
              <button className="btn-primary" onClick={() => { setShowAddForm(true); setEditingAssetId(null); setNewAsset({ assetId: '', name: '', linkedInventoryId: '', type: 'Device', status: 'Available', autoApprove: false, quantity: 1 }); }} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Plus size={16} /> Add Asset
              </button>
            </>
          )}
          <button onClick={fetchData} className="btn-outline" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <RefreshCw size={16} /> Refresh
          </button>
        </div>
        </HeaderActionsPortal>
      </div>

      {/* Add/Edit Asset Dialog -- a modal rather than an inline panel so opening it never reflows or
          pushes down the asset table behind it. */}
      {showAddForm && (
        <div
          onClick={closeAssetForm}
          style={{
            position: 'fixed', inset: 0, zIndex: 120,
            backgroundColor: 'var(--color-overlay)',
            display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
            padding: isMobile ? 0 : '2rem',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--color-bg-card)',
              borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              boxShadow: 'var(--shadow-lg)',
              width: '100%',
              maxWidth: isMobile ? '100%' : '900px',
              maxHeight: isMobile ? '92vh' : '90vh',
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: isMobile ? '0.9rem 1rem' : '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border)', flexShrink: 0 }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '600', margin: 0 }}>{editingAssetId ? t('mdmAdmin.editAsset') : t('mdmAdmin.registerAsset')}</h3>
              <button type="button" onClick={closeAssetForm} aria-label={t('common.close')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex', padding: '0.25rem' }}>
                <X size={20} />
              </button>
            </div>
          <form onSubmit={handleAddAsset} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap', padding: isMobile ? '1rem' : '1.5rem', overflowY: 'auto' }}>
            <div style={{ width: '150px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('mdmAdmin.assetIdOptional')}</label>
              <input type="text" className="input-field" value={newAsset.assetId} onChange={e => setNewAsset({...newAsset, assetId: e.target.value})} placeholder={t('mdmAdmin.autoGenerate')} />
            </div>
            <div style={{ flex: 1, minWidth: '300px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('mdmAdmin.nameOrId')}</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <select className="input-field" style={{ width: '180px' }} value={assetSource} onChange={e => {
                  setAssetSource(e.target.value);
                  setNewAsset({...newAsset, name: '', linkedInventoryId: ''});
                }}>
                  <option value="inventory">{t('mdmAdmin.fromInventory')}</option>
                  <option value="custom">{t('mdmAdmin.customItem')}</option>
                </select>
                {assetSource === 'inventory' ? (
                  <select className="input-field" value={newAsset.linkedInventoryId} onChange={e => {
                    const selectedItem = itInventory.find(i => i.id === e.target.value);
                    setNewAsset({...newAsset, linkedInventoryId: e.target.value, name: selectedItem ? selectedItem.name : ''});
                  }} required>
                    <option value="" disabled>-- Select from Inventory --</option>
                    {itInventory.map(item => <option key={item.uid || item.id} value={item.id}>{item.name} ({item.id}) ({item.quantity || 0})</option>)}
                  </select>
                ) : activeTab === 'settings' ? (
          <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? 0 : '1.5rem' }}>
            <ModuleEditPermissions
              settingsDoc="mdm_settings"
              moduleId="manage_it_assets"
              recordLabel="the IT asset register"
              approveLabel="grant or refuse device bookings"
            />
          </div>
        ) : (
                  <input type="text" className="input-field" value={newAsset.name} onChange={e => setNewAsset({...newAsset, name: e.target.value, linkedInventoryId: ''})} placeholder={t('mdmAdmin.namePlaceholder')} required />
                )}
              </div>
            </div>
            <div style={{ width: '100px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('mdmAdmin.quantity')}</label>
              <input type="number" className="input-field" value={newAsset.quantity} onChange={e => setNewAsset({...newAsset, quantity: parseInt(e.target.value) || 1})} min="1" required />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('mdmAdmin.type')}</label>
              <select className="input-field" value={newAsset.type} onChange={e => setNewAsset({...newAsset, type: e.target.value})}>
                <option value="Device">{t('mdmAdmin.hardwareDevice')}</option>
                <option value="Account">{t('mdmAdmin.digitalAccount')}</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('mdmAdmin.approvalType')}</label>
              <select className="input-field" value={newAsset.autoApprove} onChange={e => setNewAsset({...newAsset, autoApprove: e.target.value === 'true'})}>
                <option value="false">{t('mdmAdmin.requiresAdminApproval')}</option>
                <option value="true">{t('mdmAdmin.autoApproveWhenAvailable')}</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
              <button type="button" className="btn-outline" onClick={closeAssetForm}>{t('common.cancel')}</button>
              <button type="submit" className="btn-primary">{t('mdmAdmin.saveAsset')}</button>
            </div>
          </form>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div style={{ flex: 1, backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, color: 'var(--color-text-muted)' }}>
            <RefreshCw className="animate-spin" size={24} style={{ marginRight: '0.5rem' }} /> Loading MDM Data...
          </div>
        ) : activeTab === 'assets' ? (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--color-bg-app)' }}>
                <tr>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('mdmAdmin.assetId')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('mdmAdmin.assetName')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('mdmAdmin.qty')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('mdmAdmin.type')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('mdmAdmin.status')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('mdmAdmin.rules')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', textAlign: 'right' }}>{t('mdmAdmin.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {assets.length === 0 ? <tr><td colSpan="5" style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('mdmAdmin.noAssets')}</td></tr> :
                assets.map(a => (
                  <tr key={a.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '1rem', color: 'var(--color-text-muted)', fontSize: '0.875rem', fontFamily: 'monospace' }}>
                      {a.assetId || '-'}
                    </td>
                    <td style={{ padding: '1rem', fontWeight: '600' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {a.type === 'Device' ? <Laptop size={16} color="var(--color-text-muted)"/> : <Key size={16} color="var(--color-text-muted)"/>}
                        {a.name}
                      </div>
                    </td>
                    <td style={{ padding: '1rem', color: 'var(--color-text-muted)', fontWeight: '500' }}>
                      {a.dynamicAvailableQty} / {a.quantity || 1}
                    </td>
                    <td style={{ padding: '1rem', color: 'var(--color-text-muted)' }}>{a.type}</td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{ 
                        padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600',
                        backgroundColor: a.dynamicAvailableQty > 0 ? 'var(--color-success-bg)' : 'var(--color-danger-bg)',
                        color: a.dynamicAvailableQty > 0 ? 'var(--color-success-text)' : 'var(--color-danger-text)'
                      }}>
                        {a.dynamicAvailableQty > 0 ? t('mdmAdmin.available') : t('mdmAdmin.fullyBooked')}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
                      {a.autoApprove ? t('mdmAdmin.autoApprove') : t('mdmAdmin.requiresReview')}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                        {(a.quantity || 1) - a.dynamicAvailableQty > 0 && (
                          <button onClick={() => handleForceReturn(a.id)} className="btn-outline" style={{ padding: '0.4rem', fontSize: '0.75rem', width: 'auto', display: 'inline-flex' }}>{t('mdmAdmin.forceReturn')}</button>
                        )}
                        <button onClick={() => handleEditAsset(a)} className="btn-outline" style={{ padding: '0.4rem', color: 'var(--color-primary)', width: 'auto', display: 'inline-flex' }}>
                          <Edit2 size={16} />
                        </button>
                        <button onClick={() => handleDeleteAsset(a.id)} className="btn-outline" style={{ padding: '0.4rem', color: 'var(--color-danger)', width: 'auto', display: 'inline-flex' }}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--color-bg-app)' }}>
                <tr>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('mdmAdmin.assetRequested')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('mdmAdmin.user')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('mdmAdmin.timeSlot')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('mdmAdmin.status')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', textAlign: 'right' }}>{t('mdmAdmin.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {bookings.length === 0 ? <tr><td colSpan="5" style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('mdmAdmin.noBookings')}</td></tr> :
                bookings.map(b => (
                  <tr key={b.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '1rem', fontWeight: '600' }}>{b.assetName} x{b.quantity || 1}</td>
                    <td style={{ padding: '1rem' }}>{b.userId}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
                      <div style={{ marginBottom: '0.25rem' }}>{t('mdmAdmin.requested')} {b.requestDate?.toDate ? formatDateTime(b.requestDate.toDate()) : b.requestDate ? formatDateTime(new Date(b.requestDate)) : t('mdmAdmin.unknown')}</div>
                      {b.startDate && <div><Clock size={12} style={{display:'inline', verticalAlign:'middle'}}/> {b.startDate.toDate ? formatDateTime(b.startDate.toDate()) : formatDateTime(new Date(b.startDate))}</div>}
                      {b.endDate && <div><Clock size={12} style={{display:'inline', verticalAlign:'middle', opacity:0}}/> {t('mdmAdmin.to')} {b.endDate.toDate ? formatDateTime(b.endDate.toDate()) : formatDateTime(new Date(b.endDate))}</div>}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{ 
                        padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600',
                        backgroundColor: b.status === 'Reserved' ? 'var(--color-success-bg)' : b.status === 'Pending' ? 'var(--color-warning-bg)' : 'var(--color-bg-subtle)',
                        color: b.status === 'Reserved' ? 'var(--color-success-text)' : b.status === 'Pending' ? 'var(--color-warning-text)' : 'var(--color-text-subtle)'
                      }}>
                        {bookingStatusWord(b.status)}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                      {b.status === 'Pending' && (
                        <>
                          <button onClick={() => handleBookingAction(b.id, 'Reserved')} className="btn-primary" style={{ padding: '0.4rem', fontSize: '0.75rem', backgroundColor: 'var(--color-success)' }}>
                            {t('mdmAdmin.approve')}
                          </button>
                          <button onClick={() => handleBookingAction(b.id, 'Rejected')} className="btn-primary" style={{ padding: '0.4rem', fontSize: '0.75rem', backgroundColor: 'var(--color-danger)' }}>
                            {t('mdmAdmin.reject')}
                          </button>
                        </>
                      )}
                      {b.status === 'Reserved' && (
                        <button onClick={() => handleBookingAction(b.id, 'Returned')} className="btn-outline" style={{ padding: '0.4rem', fontSize: '0.75rem' }}>
                          {t('mdmAdmin.markReturned')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </div>
    </div>
  );
};

export default MDMAdmin;
